import * as z from "zod";

export const ModelConnectionProtocolSchema = z.enum([
  "openai-completions",
  "openai-responses",
  "anthropic-messages",
]);
export type ModelConnectionProtocol = z.infer<typeof ModelConnectionProtocolSchema>;
export const MODEL_CONNECTION_PROTOCOLS = [
  { value: "openai-responses", label: "/v1/responses" },
  { value: "anthropic-messages", label: "/v1/messages" },
  { value: "openai-completions", label: "/v1/chat/completions" },
] as const satisfies readonly { value: ModelConnectionProtocol; label: string }[];

/** Uses the existing generic model connection protocol and capability fields. */
export const PlatformModelSchema = z
  .object({
    provider: z.string().trim().min(1).max(100),
    protocol: ModelConnectionProtocolSchema.optional(),
    modelId: z.string().trim().min(1).max(256),
    baseUrl: z.string().url().optional(),
    enabled: z.boolean(),
    contextWindow: z.number().int().min(1024).max(2_000_000),
    maxTokens: z.number().int().min(128).max(128_000),
    acceptsImages: z.boolean(),
    reasoning: z.boolean(),
    cost: z.object({
      input: z.number().finite().nonnegative().max(1000),
      output: z.number().finite().nonnegative().max(1000),
      cacheRead: z.number().finite().nonnegative().max(1000),
      cacheWrite: z.number().finite().nonnegative().max(1000),
      // Optional on the wire for old clients; readers supply the neutral multiplier.
      multiplier: z
        .number({ error: "倍率必须大于 0" })
        .positive("倍率必须大于 0")
        .max(100, "倍率最多 100")
        .multipleOf(0.01, "倍率最多两位小数")
        .default(1)
        .optional(),
    }),
  })
  .strict()
  .refine(
    (value) => value.maxTokens < value.contextWindow,
    "Output limit must fit the context window",
  );
/** Read legacy configurations even when their old zero prices cannot be enabled today. */
export const PlatformModelWriteSchema = PlatformModelSchema.refine((value) => {
  const effective = platformModelEffectiveCost(value.cost);
  return !value.enabled || effective.input > 0 || effective.output > 0;
}, "启用模型的输入或输出价格必须大于 0");
export const PlatformModelInputSchema = z.object({
  configuration: PlatformModelWriteSchema,
  apiKey: z.string().trim().min(1).max(8192).optional(),
  expectedVersion: z.number().int().nonnegative(),
});
export const PlatformModelViewSchema = z.object({
  version: z.number().int().nonnegative(),
  configured: z.boolean(),
  configuration: PlatformModelSchema.nullable(),
});
export type PlatformModelConfiguration = z.infer<typeof PlatformModelSchema>;
export type PlatformModelPrice = PlatformModelConfiguration["cost"];

/** Runtime rates have no multiplier: consumers must not apply it a second time. */
export function platformModelEffectiveCost(cost: PlatformModelPrice) {
  const multiplier = cost.multiplier ?? 1;
  return {
    input: cost.input * multiplier,
    output: cost.output * multiplier,
    cacheRead: cost.cacheRead * multiplier,
    cacheWrite: cost.cacheWrite * multiplier,
  };
}

export const PLATFORM_MODEL_CATEGORIES = ["日常", "工作", "编码"] as const;
const boundedText = (maximum: number) =>
  z
    .string()
    .trim()
    .refine((value) => Array.from(value).length <= maximum, `最多 ${maximum} 字`);
const entryFields = {
  name: boundedText(100).refine((value) => value.length > 0, "请填写显示名称"),
  category: boundedText(20).refine((value) => value.length > 0, "请填写分类"),
  responsibilities: boundedText(200),
};
export const PlatformModelEntrySchema = z
  .object({
    id: z.string().min(1),
    version: z.number().int().positive(),
    ...entryFields,
    responsibilitiesNeedsReview: z.boolean(),
    isDefault: z.boolean(),
    hasKey: z.boolean(),
    configuration: PlatformModelSchema,
  })
  .superRefine((entry, ctx) => {
    if (!entry.isDefault && !entry.responsibilitiesNeedsReview && !entry.responsibilities)
      ctx.addIssue({ code: "custom", path: ["responsibilities"], message: "请补充负责领域" });
  });
export const PlatformModelEntryInputSchema = z
  .object({
    id: z.union([z.literal("default"), z.string().cuid()]).optional(),
    expectedVersion: z.number().int().nonnegative(),
    expectedCatalogVersion: z.number().int().nonnegative(),
    ...entryFields,
    configuration: PlatformModelWriteSchema.refine(
      (value) =>
        value.provider === "openai-compatible" && Boolean(value.protocol) && Boolean(value.baseUrl),
      "请选择兼容连接协议和接口地址",
    ),
    apiKey: z.string().trim().min(1).max(8192).optional(),
  })
  .strict();

/** The server validates the whole resulting collection, not just an edited row. */
export const PlatformModelEntriesSchema = z
  .array(PlatformModelEntrySchema)
  .superRefine((entries, ctx) => {
    const enabled = entries.filter((entry) => entry.configuration.enabled);
    if (
      entries.filter((entry) => entry.isDefault).length > 1 ||
      (enabled.length > 0 && enabled.filter((entry) => entry.isDefault).length !== 1)
    )
      ctx.addIssue({ code: "custom", message: "必须保留一个启用的默认模型" });
    const ids = new Set<string>();
    for (const entry of enabled) {
      if (ids.has(entry.configuration.modelId))
        ctx.addIssue({ code: "custom", message: "启用模型的模型 ID 不能重复" });
      ids.add(entry.configuration.modelId);
    }
  });
/** Decision services that answer the task-routing question; model names stay editable. */
export const PlatformModelRouterProviderSchema = z.enum(["typesafe", "openrouter"]);
export type PlatformModelRouterProvider = z.infer<typeof PlatformModelRouterProviderSchema>;
export const PLATFORM_MODEL_ROUTER_PROVIDERS = [
  { value: "typesafe", label: "TypeSafe", model: "jev-latest" },
  { value: "openrouter", label: "OpenRouter", model: "typesafe/jev-1.14" },
] as const satisfies readonly {
  value: PlatformModelRouterProvider;
  label: string;
  model: string;
}[];
export const PlatformModelRoutingSchema = z.object({
  allocationEnabled: z.boolean(),
  provider: PlatformModelRouterProviderSchema.nullable(),
  model: z.string().nullable(),
  hasKey: z.boolean(),
});
export const PlatformModelRoutingInputSchema = z
  .object({
    expectedCatalogVersion: z.number().int().nonnegative(),
    allocationEnabled: z.boolean(),
    provider: PlatformModelRouterProviderSchema,
    model: z.string().trim().min(1).max(100),
    apiKey: z.string().trim().min(1).max(8192).optional(),
  })
  .strict();
export const PlatformModelDefaultInputSchema = z
  .object({
    id: z.string().min(1),
    expectedCatalogVersion: z.number().int().nonnegative(),
  })
  .strict();
/** Why a run uses its platform model entry; stored on the run for the requests page. */
export const MODEL_ROUTING_OUTCOMES = [
  "default",
  "recovered-default",
  "legacy",
  "routed",
  "low-confidence",
  "router-failed",
  "single-candidate",
  "inherited",
] as const;
export type ModelRoutingOutcome = (typeof MODEL_ROUTING_OUTCOMES)[number];

export const PlatformModelCatalogSchema = z.object({
  version: z.number().int().nonnegative(),
  configured: z.boolean(),
  defaultEntryId: z.string().nullable(),
  entries: z.array(PlatformModelEntrySchema),
  /** Absent from a server that predates task routing. */
  routing: PlatformModelRoutingSchema.optional(),
});
export type PlatformModelEntry = z.infer<typeof PlatformModelEntrySchema>;
export type PlatformModelEntryInput = z.infer<typeof PlatformModelEntryInputSchema>;
export type PlatformModelRouting = z.infer<typeof PlatformModelRoutingSchema>;
export type PlatformModelRoutingInput = z.infer<typeof PlatformModelRoutingInputSchema>;

/** Measured token buckets at one operator price; absent measurements are not free usage. */
export function platformModelCost(
  usage: {
    inputTokens: number | null;
    outputTokens: number | null;
    cacheReadTokens?: number | null;
    cacheWriteTokens?: number | null;
    cacheWrite1hTokens?: number | null;
  },
  snapshot: PlatformModelPrice,
): number | null {
  const cost = platformModelEffectiveCost(snapshot);
  const measured = (value: number | null | undefined) =>
    typeof value === "number" && Number.isSafeInteger(value) && value >= 0;
  if (
    !measured(usage.inputTokens) ||
    !measured(usage.outputTokens) ||
    (cost.cacheRead > 0 && !measured(usage.cacheReadTokens))
  )
    return null;
  if (
    (usage.cacheWriteTokens != null && !measured(usage.cacheWriteTokens)) ||
    (usage.cacheWrite1hTokens != null && !measured(usage.cacheWrite1hTokens))
  )
    return null;
  return (
    (usage.inputTokens! * cost.input +
      usage.outputTokens! * cost.output +
      (usage.cacheReadTokens ?? 0) * cost.cacheRead +
      (usage.cacheWriteTokens ?? 0) * cost.cacheWrite) /
    1_000_000
  );
}

const measuredToken = z.number().int().nonnegative().nullish();
const billingUsageSchema = z.object({
  inputTokens: measuredToken,
  outputTokens: measuredToken,
  cacheReadTokens: measuredToken,
  cacheWriteTokens: measuredToken,
  totalTokens: measuredToken,
});
export const PlatformModelBillingSchema = z.object({
  multiplier: z.number(),
  totalTokens: z.number().nullable(),
  lines: z.array(
    z.object({
      kind: z.enum(["input", "output", "cacheRead", "cacheWrite"]),
      tokens: z.number().nullable(),
      basePrice: z.number(),
      effectivePrice: z.number(),
      costUsd: z.number().nullable(),
    }),
  ),
});

/** Admin-only projection. Unknown measurements stay unknown; raw receipts never leave the API. */
export function platformModelBilling(snapshot: unknown, receipt: unknown) {
  const price = PlatformModelSchema.shape.cost.safeParse(snapshot);
  if (!price.success) return null;
  const usage = billingUsageSchema.safeParse(receipt);
  const tokens = usage.success ? usage.data : {};
  const effective = platformModelEffectiveCost(price.data);
  const lines = (["input", "output", "cacheRead", "cacheWrite"] as const).map((kind) => {
    const count = tokens[`${kind}Tokens`] ?? null;
    return {
      kind,
      tokens: count,
      basePrice: price.data[kind],
      effectivePrice: effective[kind],
      costUsd: count === null ? null : (count * effective[kind]) / 1_000_000,
    };
  });
  const derivedTotal = lines.every((line) => line.tokens !== null)
    ? lines.reduce((sum, line) => sum + line.tokens!, 0)
    : null;
  return {
    multiplier: price.data.multiplier ?? 1,
    totalTokens: tokens.totalTokens ?? (Number.isSafeInteger(derivedTotal) ? derivedTotal : null),
    lines,
  };
}
